import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Trash,
  CurrencyCircleDollar,
  ListBullets,
  PlusCircle,
  Globe,
  Percent,
  Files,
} from "@phosphor-icons/react";
import { BASE_OPTIONS, CURRENCIES } from "@/lib/constants";

export default function Sidebar({
  templates,
  activeTemplateId,
  onSelectTemplate,
  onNewTemplate,
  onDuplicateTemplate,
  onDeleteTemplate,
  template,
  onUpdateTemplate,
  onAddCostLine,
  onUpdateCostLine,
  onRemoveCostLine,
}) {
  const [newLineLabel, setNewLineLabel] = useState("");

  if (!template) return null;

  const updateMeta = (patch) => onUpdateTemplate({ ...template, ...patch });

  return (
    <aside
      className="hidden lg:flex w-72 bg-neutral-50 border-r border-neutral-300 flex-col"
      style={{ height: "calc(100vh - 60px)" }}
      data-testid="sidebar"
    >
      <div className="flex-1 overflow-y-auto">
        {/* Templates list */}
        <section className="border-b border-neutral-200">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h3 className="text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-500 flex items-center gap-2">
              <Files size={12} /> Templates
            </h3>
            <button
              data-testid="new-template-button"
              onClick={onNewTemplate}
              className="text-[#002FA7] hover:text-black"
              aria-label="New template"
            >
              <PlusCircle size={18} weight="bold" />
            </button>
          </div>
          <ul className="pb-2">
            {templates.map((t) => {
              const isActive = t.id === activeTemplateId;
              return (
                <li key={t.id}>
                  <button
                    data-testid={`template-item-${t.id}`}
                    onClick={() => onSelectTemplate(t.id)}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between gap-2 group transition-colors ${
                      isActive
                        ? "bg-white border-l-2 border-[#002FA7] font-semibold"
                        : "hover:bg-neutral-100 border-l-2 border-transparent"
                    }`}
                  >
                    <span className="truncate">{t.name || "Untitled"}</span>
                    <span className="text-[10px] font-mono text-neutral-400 flex-shrink-0">
                      {t.products?.length || 0}
                    </span>
                  </button>
                </li>
              );
            })}
            {templates.length === 0 && (
              <li className="px-4 py-3 text-xs text-neutral-400">No templates yet.</li>
            )}
          </ul>
          {activeTemplateId && (
            <div className="px-4 pb-3 flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={onDuplicateTemplate}
                data-testid="duplicate-template-button"
                className="rounded-none border-neutral-300 text-xs h-7 gap-1"
              >
                <Files size={12} /> Duplicate
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="delete-template-button"
                    className="rounded-none border-neutral-300 text-xs h-7 gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash size={12} /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-display">
                      Delete this template?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently remove &quot;{template.name}&quot; and all its products.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="confirm-delete-template"
                      onClick={onDeleteTemplate}
                      className="rounded-none bg-red-600 hover:bg-red-700"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </section>

        {/* Currency settings */}
        <section className="border-b border-neutral-200 px-4 py-4 space-y-3">
          <h3 className="text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-500 flex items-center gap-2">
            <Globe size={12} /> Currency
          </h3>
          <div className="space-y-2">
            <label className="text-xs text-neutral-600 flex justify-between">
              <span>Input currency</span>
              <span className="font-mono text-neutral-400">supplier</span>
            </label>
            <Select
              value={template.inputCurrency}
              onValueChange={(v) => updateMeta({ inputCurrency: v })}
            >
              <SelectTrigger
                data-testid="input-currency-select"
                className="rounded-none border-neutral-300 font-mono h-9"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none max-h-72">
                {CURRENCIES.map((c) => (
                  <SelectItem
                    key={c.code}
                    value={c.code}
                    className="rounded-none font-mono"
                  >
                    {c.code} — {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-xs text-neutral-600 flex justify-between">
              <span>Display currency</span>
              <span className="font-mono text-neutral-400">selling</span>
            </label>
            <Select
              value={template.displayCurrency}
              onValueChange={(v) => updateMeta({ displayCurrency: v })}
            >
              <SelectTrigger
                data-testid="display-currency-select"
                className="rounded-none border-neutral-300 font-mono h-9"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none max-h-72">
                {CURRENCIES.map((c) => (
                  <SelectItem
                    key={c.code}
                    value={c.code}
                    className="rounded-none font-mono"
                  >
                    {c.code} — {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </section>

        {/* Margin */}
        <section className="border-b border-neutral-200 px-4 py-4 space-y-2">
          <h3 className="text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-500 flex items-center gap-2">
            <Percent size={12} /> Profit Margin
          </h3>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              max={99.99}
              step="0.01"
              value={template.margin}
              onChange={(e) => updateMeta({ margin: parseFloat(e.target.value) || 0 })}
              data-testid="margin-input"
              className="rounded-none border-neutral-300 font-mono h-9 text-right"
            />
            <span className="font-mono text-sm text-neutral-500">%</span>
          </div>
          <p className="text-[11px] text-neutral-500 leading-snug">
            Margin is applied on selling price.<br />
            <span className="font-mono">Sell = Cost ÷ (1 − margin%)</span>
          </p>
        </section>

        {/* Cost Lines editor */}
        <section className="px-4 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-500 flex items-center gap-2">
              <ListBullets size={12} /> Cost Lines
            </h3>
            <span className="text-[10px] font-mono text-neutral-400">
              {template.costLines.length}
            </span>
          </div>

          <div className="space-y-2">
            {template.costLines.map((line, idx) => (
              <div
                key={line.id}
                data-testid={`cost-line-${line.id}`}
                className="border border-neutral-300 bg-white p-2 space-y-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <Input
                    value={line.label}
                    onChange={(e) =>
                      onUpdateCostLine(idx, { ...line, label: e.target.value })
                    }
                    placeholder="Label"
                    data-testid={`cost-line-label-${line.id}`}
                    className="rounded-none border-neutral-200 h-7 text-xs font-medium px-2"
                  />
                  <button
                    onClick={() => onRemoveCostLine(idx)}
                    data-testid={`remove-cost-line-${line.id}`}
                    aria-label="Remove cost line"
                    className="text-neutral-400 hover:text-red-600 flex-shrink-0"
                  >
                    <Trash size={14} />
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <Select
                    value={line.type}
                    onValueChange={(v) =>
                      onUpdateCostLine(idx, { ...line, type: v })
                    }
                  >
                    <SelectTrigger
                      data-testid={`cost-line-type-${line.id}`}
                      className="rounded-none border-neutral-200 h-7 text-xs px-2 w-20"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="percent" className="rounded-none text-xs">
                        %
                      </SelectItem>
                      <SelectItem value="flat" className="rounded-none text-xs">
                        Flat
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    step="0.01"
                    value={line.value}
                    onChange={(e) =>
                      onUpdateCostLine(idx, {
                        ...line,
                        value: parseFloat(e.target.value) || 0,
                      })
                    }
                    data-testid={`cost-line-value-${line.id}`}
                    className="rounded-none border-neutral-200 h-7 text-xs font-mono text-right px-2"
                  />
                </div>
                {line.type === "percent" && (
                  <Select
                    value={line.base}
                    onValueChange={(v) =>
                      onUpdateCostLine(idx, { ...line, base: v })
                    }
                  >
                    <SelectTrigger
                      data-testid={`cost-line-base-${line.id}`}
                      className="rounded-none border-neutral-200 h-7 text-[11px] px-2"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {BASE_OPTIONS.map((b) => (
                        <SelectItem
                          key={b.value}
                          value={b.value}
                          className="rounded-none text-xs"
                        >
                          {b.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newLineLabel.trim()) return;
              onAddCostLine(newLineLabel.trim());
              setNewLineLabel("");
            }}
            className="flex items-center gap-1"
          >
            <Input
              value={newLineLabel}
              onChange={(e) => setNewLineLabel(e.target.value)}
              placeholder="e.g. Insurance"
              data-testid="new-cost-line-input"
              className="rounded-none border-neutral-300 h-8 text-xs"
            />
            <Button
              type="submit"
              data-testid="add-cost-line-button"
              size="sm"
              className="rounded-none bg-neutral-900 hover:bg-black h-8 px-2 text-white"
              aria-label="Add cost line"
            >
              <Plus size={14} weight="bold" />
            </Button>
          </form>
        </section>
      </div>
    </aside>
  );
}
